/**
 * Keyline — Interlock's one board look (owner amendment, 2026-10-04;
 * `docs/DESIGN.md`, "The board"; `docs/decisions/0056`). It replaces the six
 * named themes, and carries Concrete's colour-blind mechanism into the only
 * look there is:
 *
 * - **Seven hues chosen to stay apart for everybody.** The pieces are the
 *   Okabe-Ito palette (Wong, "Points of view: Color blindness", Nature
 *   Methods 8, 441, 2011), designed to stay distinct under protanopia,
 *   deuteranopia and tritanopia. `apps/client/test/render/board-look.test.ts`
 *   simulates all three and holds every pair apart.
 * - **Stripes on S and Z**, the two pieces the classic red/green pairing
 *   makes hardest to tell apart: S striped one way, Z the other, so they
 *   differ by pattern as well as by colour.
 * - **A 2px keyline round every cell**, so a block reads against the board
 *   by its edge even where its fill is close to the ground (the yellow on a
 *   light board), and two blocks of one piece read as cells.
 * - **Flat fills, no glow, cuts instead of fades**: nothing here touches
 *   `ctx.globalAlpha` or `ctx.shadowBlur`.
 *
 * Two variants, one look: the board follows the app's appearance. Dark is a
 * neutral near-black board, Light a neutral near-white one, both untinted,
 * so the board sits on the shell's grounds whichever they are (today's
 * Lagoon ladder, or a pure black and a pure white). The pieces, the keyline
 * and the garbage are the same in both; the ground, the ink (ghost, labels,
 * the clear's cut) and the board's accent follow the variant.
 */
import { ghostByCell } from './paint-utils.js';
import type {
  CellRect,
  PaintKind,
  PaintOptions,
  PieceColours,
  Theme,
  ThemeTokens,
} from './types.js';

/** The board's two variants, named as the app's appearances are. */
export type BoardBase = 'dark' | 'light';

/** The keyline round every cell, and the stripes of S and Z: one near-black in both variants. */
export const KEYLINE = '#111111';

/** The seven pieces: Okabe-Ito, the same in both variants. */
export const KEYLINE_PIECES: PieceColours = {
  I: '#56B4E9', // sky blue
  O: '#F0E442', // yellow
  T: '#CC79A7', // reddish purple
  S: '#009E73', // bluish green, striped
  Z: '#D55E00', // vermillion, striped
  J: '#0072B2', // blue
  L: '#E69F00', // orange
};

/** Garbage: a neutral grey, apart from every piece and readable on both grounds. */
export const KEYLINE_GARBAGE = '#6E6E6E';

const DARK: ThemeTokens = {
  id: 'keyline-dark',
  label: 'Keyline (dark)',
  background: '#0A0A0A',
  accent: '#5F92FF',
  garbage: KEYLINE_GARBAGE,
  foreground: '#EDEDED',
  pieces: KEYLINE_PIECES,
};

const LIGHT: ThemeTokens = {
  id: 'keyline-light',
  label: 'Keyline (light)',
  background: '#F5F5F5',
  accent: '#2D5FD6',
  garbage: KEYLINE_GARBAGE,
  foreground: '#141414',
  pieces: KEYLINE_PIECES,
};

/** S one way, Z the other: the pattern that tells them apart without colour. */
const STRIPE_ANGLE: Readonly<Partial<Record<PaintKind, 1 | -1>>> = { S: 1, Z: -1 };

function fillFor(tokens: ThemeTokens, kind: PaintKind): string {
  return kind === 'G' ? tokens.garbage : tokens.pieces[kind];
}

function paintStripes(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  angleSign: 1 | -1,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();
  ctx.fillStyle = KEYLINE;
  const stripe = Math.max(2, Math.round(size * 0.12));
  const span = size * 2;
  ctx.translate(x + size / 2, y + size / 2);
  ctx.rotate(angleSign * (Math.PI / 4));
  for (let offset = -span; offset < span; offset += stripe * 3) {
    ctx.fillRect(offset, -span, stripe, span * 2);
  }
  ctx.restore();
}

function painterFor(tokens: ThemeTokens): Theme['paintCell'] {
  const ink = tokens.foreground ?? KEYLINE;
  return (
    ctx: CanvasRenderingContext2D,
    rect: CellRect,
    kind: PaintKind,
    options: PaintOptions,
  ) => {
    const { x, y, size } = rect;

    if (options.state.kind === 'ghost') {
      // An outline in the board's ink, dashed: where the piece will land.
      ctx.save();
      ctx.strokeStyle = ink;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(x + 1.5, y + 1.5, Math.max(0, size - 3), Math.max(0, size - 3));
      ctx.setLineDash([]);
      ctx.restore();
      return;
    }

    const progress = options.state.kind === 'clearing' ? options.state.progress : 0;

    ctx.save();
    if (progress > 0) {
      // Cuts, not fades: a flat swap to the ink, and a hard slide.
      ctx.fillStyle = ink;
      ctx.fillRect(x + progress * size * 1.4, y, size, size);
    } else {
      ctx.fillStyle = fillFor(tokens, kind);
      ctx.fillRect(x, y, size, size);
      const angle = STRIPE_ANGLE[kind];
      if (angle !== undefined) paintStripes(ctx, x, y, size, angle);
    }
    ctx.strokeStyle = KEYLINE;
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, Math.max(0, size - 2), Math.max(0, size - 2));
    ctx.restore();
  };
}

function themeOf(tokens: ThemeTokens): Theme {
  const paintCell = painterFor(tokens);
  return { tokens, paintCell, paintGhost: ghostByCell(paintCell) };
}

/** The dark board: a neutral near-black ground. */
export const keylineDark: Theme = themeOf(DARK);

/** The light board: a neutral near-white ground. */
export const keylineLight: Theme = themeOf(LIGHT);
